import { useConsumptionTabProjection } from "./ConsumptionTabProjectionContext";
import { Alert, Box, Stack, Typography, useTheme } from "@mui/material";
import { StatCard } from "components/common/StatCard";
import { DateStepper } from "components/DateStepper";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { ConsumptionProjectionChart } from "./ConsumptionProjectionChart";
import { SelectBox } from "../Selectbox";
import {
  getAvailablePriceSources,
  getWeightedPrice,
} from "./consumptionProjectionUtils";
import { useMemo, useState } from "react";
import { PriceSourceName } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { TFunction } from "i18next";
import { ConsumptionDisplayModeToggle } from "./ConsumptionDisplayModeToggle";
import { ConsumptionDisplayMode } from "./consumptionTypes";

const PREFERRED_DEFAULT_SOURCE = "inventoryReportSource";

export const ConsumptionProjection = () => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const units = useUnitsFormatter(false);
  const {
    data,
    selectedDate,
    setSelectedDate,
    isLoading,
    isError,
    isFetching,
  } = useConsumptionTabProjection();

  const availablePriceSources = useMemo(
    () => getAvailablePriceSources(data),
    [data]
  );

  const [selectedPriceSource, setSelectedPriceSource] =
    useState<PriceSourceName | null>(null);

  const [displayMode, setDisplayMode] =
    useState<ConsumptionDisplayMode>("percentage");

  const effectiveSource =
    selectedPriceSource !== null &&
    availablePriceSources.includes(selectedPriceSource)
      ? selectedPriceSource
      : availablePriceSources.includes(PREFERRED_DEFAULT_SOURCE)
        ? PREFERRED_DEFAULT_SOURCE
        : (availablePriceSources[0] ?? null);

  const projectionPrice =
    effectiveSource !== null
      ? getWeightedPrice(data, "projectionSource", effectiveSource)
      : null;
  const trajectoryPrice =
    effectiveSource !== null
      ? getWeightedPrice(data, "trajectorySource", effectiveSource)
      : null;

  const priceDifference =
    projectionPrice !== null && trajectoryPrice !== null
      ? trajectoryPrice - projectionPrice
      : null;

  if (isError) {
    return (
      <Box>
        <Alert severity="error">{t("errorLoadingData")}</Alert>
      </Box>
    );
  }

  return (
    <Stack direction="column" spacing={2} alignItems="center">
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        sx={{ width: "100%" }}
      >
        <Box
          sx={{
            flex: 1,
            pl: 8,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ConsumptionDisplayModeToggle
            value={displayMode}
            onChange={setDisplayMode}
          />
        </Box>
        <DateStepper date={selectedDate} setDate={setSelectedDate} />
        <Stack
          sx={{ flex: 1, pr: 8 }}
          spacing={2}
          alignItems="center"
          direction="column"
        >
          {availablePriceSources.length > 0 && (
            <Stack spacing={2} sx={{ width: "fit-content" }}>
              <PriceSummary
                projectionPrice={projectionPrice}
                trajectoryPrice={trajectoryPrice}
                priceDifference={priceDifference}
                formatPrice={formatNumber(2, true)}
                unitLabel={units("specific_cost")}
                t={t}
              />
              <Box
                sx={{
                  width: "100%",
                  "& .MuiFormControl-root": {
                    minWidth: 0,
                    width: "100%",
                    mt: 0,
                  },
                }}
              >
                <SelectBox<PriceSourceName>
                  availableOptions={availablePriceSources}
                  selectedOption={effectiveSource}
                  onOptionChange={setSelectedPriceSource}
                  id="cost-base-select"
                  labelId="cost-base-label"
                  label={t("costBase")}
                />
              </Box>
            </Stack>
          )}
        </Stack>
      </Stack>
      <ConsumptionProjectionChart
        data={data}
        isLoading={isLoading || isFetching}
        displayMode={displayMode}
        layout={{
          autosize: true,
          margin: {
            l: 150,
          },
          barmode: "group",
          showlegend: true,
          legend: {
            orientation: "h",
            yanchor: "bottom",
            y: 1.02,
            xanchor: "center",
            x: 0.5,
          },
          yaxis: {
            autorange: "reversed",
          },
        }}
        style={{ width: "70vw", height: "60vh" }}
      />
    </Stack>
  );
};

const PriceSummary = ({
  projectionPrice,
  trajectoryPrice,
  priceDifference,
  formatPrice,
  unitLabel,
  t,
}: {
  projectionPrice: number | null;
  trajectoryPrice: number | null;
  priceDifference: number | null;
  formatPrice: (value?: number | null) => string;
  unitLabel: string;
  t: TFunction;
}) => {
  const theme = useTheme();

  if (projectionPrice === null && trajectoryPrice === null) return null;

  const diffColor =
    priceDifference !== null && priceDifference !== 0
      ? priceDifference < 0
        ? theme.palette.success.main
        : theme.palette.error.main
      : theme.palette.text.secondary;

  const diffSign = priceDifference !== null && priceDifference >= 0 ? "+" : "";
  const diffLabel =
    priceDifference !== null
      ? `(${diffSign}${formatPrice(priceDifference)})`
      : null;

  return (
    <Stack direction="row" spacing={2}>
      {projectionPrice !== null && (
        <StatCard
          label={t("plannedAfterBuying")}
          value={`${formatPrice(projectionPrice)} ${unitLabel}`}
        />
      )}
      {trajectoryPrice !== null && (
        <StatCard
          label={t("eomProjected")}
          value={`${formatPrice(trajectoryPrice)} ${unitLabel}`}
          footer={
            diffLabel !== null ? (
              <Typography variant="caption" sx={{ color: diffColor }}>
                {diffLabel}
              </Typography>
            ) : undefined
          }
        />
      )}
    </Stack>
  );
};
